import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, UserPlus, Shield, ShieldCheck, Send, Trash2, MessageCircle, Power, Loader2 } from "lucide-react";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  role: "operator",
};

export default function Usuarios() {
  const { t } = useT();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/users/invite", form);
      if (r.data?.invite_sent) {
        toast.success(t("users.inviteSent", { name: form.name }));
      } else {
        toast.warning(t("users.inviteCreatedNoWhatsapp", { error: r.data?.invite_error || "" }));
      }
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/resend-invite`);
      if (r.data?.invite_sent) toast.success(t("users.resendOk"));
      else toast.warning(t("users.resendFailed", { error: r.data?.invite_error || "" }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(t("common.updated"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    try {
      await api.put(`/users/${u.id}`, { role: newRole });
      toast.success(t("common.updated"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(t("users.confirmDelete", { name: u.name }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("users.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("users.pageTitle", { n: users.length })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("users.pageSubtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-black" data-testid="invite-user-button">
              <UserPlus className="w-4 h-4 mr-2" /> {t("users.invite")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md rounded-none">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">{t("users.inviteTitle")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={invite} className="space-y-4">
              <Field label={t("users.name")}>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-none" data-testid="invite-name-input" />
              </Field>
              <Field label={t("users.email")}>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                  className="rounded-none" data-testid="invite-email-input" />
              </Field>
              <Field label={t("users.phoneE164")}>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+15614556277" className="rounded-none font-mono" data-testid="invite-phone-input" />
                <p className="text-[10px] text-zinc-500 mt-1">{t("users.phoneHint")}</p>
              </Field>
              <Field label={t("users.role")}>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-none" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{t("users.roleOperator")}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#F5A623]" />
                        <span>{t("users.roleAdmin")}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {form.role === "admin" ? t("users.roleAdminHint") : t("users.roleOperatorHint")}
                </p>
              </Field>
              <div className="bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-xs text-emerald-900 flex items-start gap-2">
                <MessageCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>{t("users.whatsappHint")}</div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={submitting} className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-black" data-testid="invite-submit-button">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {t("users.sendInvite")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="border border-zinc-200 p-16 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-400" />
        </div>
      ) : users.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <UserPlus className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("users.emptyTitle")}</div>
          <p className="text-sm text-zinc-500">{t("users.emptySub")}</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("users.name")}</Th>
                <Th>{t("users.email")}</Th>
                <Th>{t("users.phone")}</Th>
                <Th>{t("users.role")}</Th>
                <Th>{t("common.status")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isPending = u.status === "pending";
                const isInactive = u.active === false;
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                    <Td>
                      <div className="flex items-center gap-2">
                        {u.role === "admin" ? <ShieldCheck className="w-3.5 h-3.5 text-[#F5A623]" /> : <Shield className="w-3.5 h-3.5 text-zinc-400" />}
                        <span className="font-medium">{u.name}</span>
                        {isSelf && <span className="text-[10px] font-mono text-zinc-400">({t("users.you")})</span>}
                      </div>
                    </Td>
                    <Td className="font-mono text-xs">{u.email}</Td>
                    <Td className="font-mono text-xs">{u.phone || <span className="text-zinc-400">—</span>}</Td>
                    <Td>
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                        <SelectTrigger className="rounded-none h-8 text-xs w-32" data-testid={`role-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operator">{t("users.roleOperator")}</SelectItem>
                          <SelectItem value="admin">{t("users.roleAdmin")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border bg-amber-100 text-amber-800 border-amber-300">
                          {t("users.statusPending")}
                        </span>
                      ) : isInactive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border bg-zinc-200 text-zinc-700 border-zinc-300">
                          {t("users.statusInactive")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border bg-emerald-100 text-emerald-800 border-emerald-300">
                          {t("users.statusActive")}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center justify-end gap-1.5">
                        {isPending && (
                          <button
                            onClick={() => resend(u)}
                            className="p-1.5 border border-zinc-200 hover:border-[#F5A623] hover:text-[#F5A623]"
                            title={t("users.resendInvite")}
                            data-testid={`resend-${u.email}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!isPending && !isSelf && (
                          <button
                            onClick={() => toggleActive(u)}
                            className={`p-1.5 border border-zinc-200 ${isInactive ? "hover:border-emerald-500 hover:text-emerald-600" : "hover:border-amber-500 hover:text-amber-600"}`}
                            title={isInactive ? t("users.activate") : t("users.deactivate")}
                            data-testid={`toggle-${u.email}`}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => remove(u)}
                            className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600"
                            title={t("common.delete")}
                            data-testid={`delete-${u.email}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="overline">{label}</label>
    {children}
  </div>
);
